import { useEffect, useState } from 'react';
import { Refine, Authenticated } from '@refinedev/core';
import { useNotificationProvider } from '@refinedev/mui';
import { SnackbarProvider } from 'notistack';
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { blue } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import routerBindings, { UnsavedChangesNotifier } from '@refinedev/react-router-v6';

import { authProvider } from '@/providers/authProvider';
import { dataProvider } from '@/providers/dataProvider';
import { UserProvider } from '@/context/UserContext';
import { SessionProvider } from '@/context/SessionContext';
import { setAuthRefreshFailedHandler, setPermissionDeniedHandler } from '@/api/axiosInstance';

import { AppLayout } from '@/components/layout/AppLayout';
import { RootLayout } from '@/components/layout/RootLayout';
import { AuthErrorDialog } from '@/components/shared/AuthErrorDialog';
import { SessionDialog } from '@/components/shared/SessionDialog';
import { SplashScreen } from '@/pages/splash-screen';
import { OrdersModule } from '@/pages/orders';
import { OrderDetailsPage } from '@/pages/orders/detail';
import { Scheduling } from '@/pages/scheduling';
import { SupplyPoint } from '@/pages/supply-point';
import { InventoryManagementPage } from '@/pages/inventory-management';

const muiTheme = createTheme({
  palette: {
    primary: blue,
    mode: 'light',
  },
  typography: { fontFamily: "'Roboto', sans-serif" },
});

export const App = () => {
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [authRefreshFailed, setAuthRefreshFailed] = useState(false);

  useEffect(() => {
    setPermissionDeniedHandler(() => setPermissionDenied(true));
    setAuthRefreshFailedHandler(() => setAuthRefreshFailed(true));
  }, []);

  return (
    <HashRouter>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            html: { WebkitFontSmoothing: 'auto' },
            body: { margin: 0, padding: 0, backgroundColor: '#f2f4f6', fontFamily: "'Roboto', sans-serif" },
            '.im-page-header': {
              backgroundColor: '#0c479d',
              color: 'white',
              minHeight: '80px',
              display: 'flex',
              alignItems: 'center',
              padding: '0 24px',
              gap: '16px',
            },
            '.im-page-header-title': {
              fontSize: '34px !important',
              fontWeight: 'normal !important',
              lineHeight: '45px !important',
              color: 'white !important',
              margin: 0,
              fontFamily: 'Roboto, sans-serif',
            },
            '.im-tabs-bar': {
              backgroundColor: 'white',
              minHeight: '48px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              padding: '0 20px',
            },
            '.im-tabs-bar .MuiTabs-indicator': {
              backgroundColor: '#0c479d',
            },
            '.im-tabs-bar .MuiTab-root.Mui-selected': {
              color: '#0c479d',
            },
            '.im-tabs-bar .MuiTab-root': {
              minWidth: '0 !important',
              paddingLeft: '10px !important',
              paddingRight: '10px !important',
              marginLeft: '10px !important',
              marginRight: '10px !important',
              letterSpacing: '.08em !important',
              fontSize: '14px !important',
            },
          }}
        />
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <RefineWrapper permissionDenied={permissionDenied} authRefreshFailed={authRefreshFailed} />
        </SnackbarProvider>
      </ThemeProvider>
    </HashRouter>
  );
};

const RefineWrapper = ({
  permissionDenied,
  authRefreshFailed,
}: {
  permissionDenied: boolean;
  authRefreshFailed: boolean;
}) => {
  const notificationProvider = useNotificationProvider();

  return (
    <Refine
      authProvider={authProvider}
      dataProvider={dataProvider}
      routerProvider={routerBindings}
      notificationProvider={notificationProvider}
      resources={[
        {
          name: 'orders',
          list: '/orders',
          create: '/orders/new',
          edit: '/orders/:id',
        },
        { name: 'scheduling', list: '/scheduling' },
        { name: 'supply-point', list: '/supply-point' },
      ]}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: true,
      }}
    >
      <SessionProvider>
        <UserProvider>
          <SessionDialog open={permissionDenied} />
          <AuthErrorDialog open={authRefreshFailed} />
          <Routes>
            <Route element={<RootLayout />}>
              <Route path="/" element={<SplashScreen />} />

              <Route
                element={(
                  <Authenticated key="authenticated-layout" fallback={<Navigate to="/" replace />}>
                    <AppLayout />
                  </Authenticated>
                )}
              >
                <Route path="/orders" element={<OrdersModule />} />
                <Route path="/orders/new" element={<OrderDetailsPage mode="new" />} />
                <Route path="/orders/:id" element={<OrderDetailsPage />} />
                <Route path="/scheduling" element={<Scheduling />} />
                <Route path="/supply-point" element={<SupplyPoint />} />
                <Route path="/inventory-management" element={<InventoryManagementPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <UnsavedChangesNotifier />
        </UserProvider>
      </SessionProvider>
    </Refine>
  );
};